import { Link } from "react-router-dom";
import { ArrowRight, Headphones, Users, Bookmark, Share2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { t, fmtDate } from "@/i18n";
import { Change, fmtNum } from "@/components/analytics/Widgets";
import { CountUp, Spotlight } from "./motion";

const Mini = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-3 min-w-0" data-testid={testId}>
    <span className="h-8 w-8 shrink-0 rounded-lg bg-muted grid place-items-center text-muted-foreground"><Icon className="h-3.5 w-3.5" /></span>
    <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</p><p className="font-display font-bold text-lg leading-tight"><CountUp value={value} /></p></div>
  </div>
);

const Tip = ({ active, payload, label }) => active && payload?.length ? (
  <div className="rounded-lg border border-border bg-popover/95 backdrop-blur px-3 py-2 text-xs shadow-xl">
    <p className="text-muted-foreground">{fmtDate(label, { day: "numeric", month: "short" })}</p>
    <p className="font-semibold tabular-nums">{fmtNum(payload[0].value)} streams</p>
  </div>) : null;

export const StreamsPanel = ({ data }) => {
  const totals = data?.totals || {};
  const daily = (data?.daily || []).slice(-30);
  const sum = (n) => daily.slice(-n).reduce((a, d) => a + (d.streams || 0), 0);
  const streams7 = totals.streams_7d || sum(7);
  const streams30 = totals.streams_30d || sum(30);
  const top = data?.releases?.[0];
  const empty = !data || !data.releases?.length;
  return (
    <Spotlight className="p-6 lg:col-span-8 overflow-hidden" data-testid="streams-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="eyebrow">{t("Streams · últimos 7 días")}</p>
          <div className="flex items-baseline gap-3 mt-1">
            <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight"><CountUp value={empty ? 0 : streams7} testId="streams-7d-value" /></p>
            {top?.streams_7d?.change_pct != null && <Change pct={top.streams_7d.change_pct} testId="streams-change" />}
          </div>
        </div>
        <Link to="/analiticas" className="text-sm text-primary flex items-center gap-1 hover:underline link-arrow" data-testid="link-analytics">{t("Ver analíticas")} <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
      {empty ? (
        <div className="h-44 grid place-items-center text-sm text-muted-foreground text-center px-6" data-testid="streams-empty">
          {t("Las analíticas aparecerán aquí cuando tus lanzamientos estén aprobados y empiecen a sonar.")}
        </div>
      ) : (
        <div className="h-44 -mx-2 chart-reveal">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={daily} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <defs><linearGradient id="dashStreams" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} /><stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" hide /><YAxis hide domain={[0, "dataMax + 5"]} />
              <Tooltip content={<Tip />} cursor={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.3 }} />
              <Area type="monotone" dataKey="streams" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#dashStreams)" animationDuration={1400} dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-border">
        <Mini icon={Headphones} label={t("30 días")} value={empty ? 0 : streams30} testId="mini-streams-30d" />
        <Mini icon={Users} label={t("Oyentes")} value={totals.listeners_7d || 0} testId="mini-listeners" />
        <Mini icon={Bookmark} label={t("Guardados")} value={totals.saves_7d || 0} testId="mini-saves" />
        <Mini icon={Share2} label={t("Vistas sociales")} value={totals.social_views_7d || 0} testId="mini-social" />
      </div>
    </Spotlight>
  );
};
